/**
 * AI Recruit - Question Type Management Page
 * Admin page for managing proctored-interview question types and their
 * sub-types (e.g. Coding, Design, Verbal -> Technical Questions / Scenarios).
 */

import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import {
  PlusIcon, PencilIcon, CheckCircleIcon, XCircleIcon, ChevronDownIcon, ChevronRightIcon,
  ClockIcon, XMarkIcon,
} from '@heroicons/react/24/outline'

import LoadingSpinner from '@/components/common/LoadingSpinner'
import {
  fetchQuestionTypes, createQuestionType, updateQuestionType, activateQuestionType, deactivateQuestionType,
  createSubtype, updateSubtype, activateSubtype, deactivateSubtype, getQuestionTypeAuditLog,
  type QuestionType, type QuestionSubtype, type QuestionTypeAuditLogEntry,
} from '@/services/questionTypeApi'
import { usePermission } from '@/hooks/usePermission'

const CATEGORY_OPTIONS = ['coding', 'design', 'verbal']
const RESPONSE_MODE_OPTIONS: { value: string; label: string }[] = [
  { value: 'verbal', label: 'Verbal (spoken answer)' },
  { value: 'text', label: 'Text answer' },
  { value: 'code_editor', label: 'Code editor' },
  { value: 'whiteboard', label: 'Whiteboard (diagram)' },
]

interface TypeFormState {
  name: string
  description: string
  category: string
  response_mode: string
  default_duration_minutes: number
}

const EMPTY_TYPE_FORM: TypeFormState = {
  name: '', description: '', category: 'verbal', response_mode: 'verbal', default_duration_minutes: 10,
}

interface SubtypeFormState {
  name: string
  description: string
  default_duration_minutes: string
}

const EMPTY_SUBTYPE_FORM: SubtypeFormState = { name: '', description: '', default_duration_minutes: '' }

const QuestionTypeManagementPage: React.FC = () => {
  // questionTypes.manage isn't in the RBAC catalog yet (new feature), so
  // this mirrors the backend's gated()/legacy_check admin-tier fallback
  // rather than a capability that would resolve to false for every role —
  // see OrgSettingsLayout's canSeeQuestionTypes for the same reasoning.
  const { role, isBypassAll } = usePermission()
  const canManage = isBypassAll || (!!role && ['super_admin', 'admin', 'manager'].includes(role))
  const queryClient = useQueryClient()

  const [expanded, setExpanded] = useState<Record<string, boolean>>({})
  const [showCreateType, setShowCreateType] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  const [editingType, setEditingType] = useState<QuestionType | null>(null)
  const [typeForm, setTypeForm] = useState<TypeFormState>(EMPTY_TYPE_FORM)
  const [subtypeFormFor, setSubtypeFormFor] = useState<string | null>(null)
  const [editingSubtype, setEditingSubtype] = useState<QuestionSubtype | null>(null)
  const [subtypeForm, setSubtypeForm] = useState<SubtypeFormState>(EMPTY_SUBTYPE_FORM)

  const { data: questionTypes, isLoading } = useQuery({
    queryKey: ['question-types'],
    queryFn: () => fetchQuestionTypes(true),
  })

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['question-types'] })

  const createTypeMutation = useMutation({
    mutationFn: createQuestionType,
    onSuccess: () => { toast.success('Question type created'); invalidate(); closeTypeForm() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Failed to create question type'),
  })
  const updateTypeMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<TypeFormState> }) => updateQuestionType(id, data),
    onSuccess: () => { toast.success('Question type updated'); invalidate(); closeTypeForm() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Failed to update question type'),
  })
  const toggleTypeMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      active ? deactivateQuestionType(id) : activateQuestionType(id),
    onSuccess: () => { invalidate() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Failed to update status'),
  })

  const createSubtypeMutation = useMutation({
    mutationFn: ({ typeId, data }: { typeId: string; data: any }) => createSubtype(typeId, data),
    onSuccess: () => { toast.success('Subtype created'); invalidate(); closeSubtypeForm() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Failed to create subtype'),
  })
  const updateSubtypeMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) => updateSubtype(id, data),
    onSuccess: () => { toast.success('Subtype updated'); invalidate(); closeSubtypeForm() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Failed to update subtype'),
  })
  const toggleSubtypeMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      active ? deactivateSubtype(id) : activateSubtype(id),
    onSuccess: () => { invalidate() },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Failed to update status'),
  })

  const closeTypeForm = () => {
    setShowCreateType(false)
    setEditingType(null)
    setTypeForm(EMPTY_TYPE_FORM)
  }
  const closeSubtypeForm = () => {
    setSubtypeFormFor(null)
    setEditingSubtype(null)
    setSubtypeForm(EMPTY_SUBTYPE_FORM)
  }

  const startEditType = (qt: QuestionType) => {
    setEditingType(qt)
    setShowCreateType(false)
    setTypeForm({
      name: qt.name, description: qt.description || '', category: qt.category,
      response_mode: qt.response_mode, default_duration_minutes: qt.default_duration_minutes,
    })
  }

  const startEditSubtype = (st: QuestionSubtype) => {
    setEditingSubtype(st)
    setSubtypeFormFor(st.question_type_id)
    setSubtypeForm({
      name: st.name, description: st.description || '',
      default_duration_minutes: st.default_duration_minutes != null ? String(st.default_duration_minutes) : '',
    })
  }

  const submitTypeForm = () => {
    if (!typeForm.name.trim()) { toast.error('Name is required'); return }
    if (editingType) {
      updateTypeMutation.mutate({ id: editingType.id, data: typeForm })
    } else {
      createTypeMutation.mutate(typeForm)
    }
  }

  const submitSubtypeForm = (typeId: string) => {
    if (!subtypeForm.name.trim()) { toast.error('Name is required'); return }
    const data = {
      name: subtypeForm.name,
      description: subtypeForm.description || undefined,
      default_duration_minutes: subtypeForm.default_duration_minutes ? Number(subtypeForm.default_duration_minutes) : undefined,
    }
    if (editingSubtype) {
      updateSubtypeMutation.mutate({ id: editingSubtype.id, data })
    } else {
      createSubtypeMutation.mutate({ typeId, data })
    }
  }

  if (isLoading) {
    return <div className="flex justify-center py-12"><LoadingSpinner size="lg" /></div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Question Type Management</h2>
          <p className="text-sm text-gray-500">
            Manage the question types (and their sub-types) available when building a proctored interview.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowHistory(true)}
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
          >
            <ClockIcon className="w-4 h-4" /> History
          </button>
          {canManage && (
            <button
              onClick={() => { setShowCreateType(true); setEditingType(null); setTypeForm(EMPTY_TYPE_FORM) }}
              className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
            >
              <PlusIcon className="w-4 h-4" /> Add Question Type
            </button>
          )}
        </div>
      </div>

      {showHistory && <HistoryModal onClose={() => setShowHistory(false)} />}

      {(showCreateType || editingType) && (
        <div className="bg-white border border-gray-200 rounded-lg p-4 space-y-3">
          <h3 className="text-sm font-semibold text-gray-900">{editingType ? 'Edit Question Type' : 'New Question Type'}</h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
              <input
                value={typeForm.name}
                onChange={(e) => setTypeForm({ ...typeForm, name: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm"
                placeholder="e.g. Coding"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
              <select
                value={typeForm.category}
                onChange={(e) => setTypeForm({ ...typeForm, category: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm"
              >
                {CATEGORY_OPTIONS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Candidate response mode</label>
              <select
                value={typeForm.response_mode}
                onChange={(e) => setTypeForm({ ...typeForm, response_mode: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm"
              >
                {RESPONSE_MODE_OPTIONS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Default duration (minutes)</label>
              <input
                type="number" min={1} max={180}
                value={typeForm.default_duration_minutes}
                onChange={(e) => setTypeForm({ ...typeForm, default_duration_minutes: Number(e.target.value) })}
                className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
              <textarea
                value={typeForm.description}
                onChange={(e) => setTypeForm({ ...typeForm, description: e.target.value })}
                className="w-full border border-gray-300 rounded-md px-3 py-1.5 text-sm"
                rows={2}
                placeholder="What this question type evaluates and how"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button onClick={submitTypeForm} className="px-3 py-1.5 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700">
              {editingType ? 'Save' : 'Create'}
            </button>
            <button onClick={closeTypeForm} className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-md text-sm hover:bg-gray-200">
              Cancel
            </button>
          </div>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-lg divide-y divide-gray-100">
        {(questionTypes || []).length === 0 && (
          <div className="p-6 text-center text-sm text-gray-500">No question types yet.</div>
        )}
        {(questionTypes || []).map((qt) => (
          <div key={qt.id} className={!qt.is_active ? 'opacity-60' : ''}>
            <div className="flex items-center justify-between px-4 py-3">
              <button
                onClick={() => setExpanded({ ...expanded, [qt.id]: !expanded[qt.id] })}
                className="flex items-center gap-2 text-left flex-1 min-w-0"
              >
                {expanded[qt.id] ? <ChevronDownIcon className="w-4 h-4 text-gray-400 flex-shrink-0" /> : <ChevronRightIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />}
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-900">{qt.name}</span>
                    <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">{qt.category}</span>
                    <span className="text-xs text-gray-400">{qt.default_duration_minutes} min default</span>
                    {!qt.is_active && <span className="text-xs px-1.5 py-0.5 bg-red-50 text-red-600 rounded">Inactive</span>}
                  </div>
                  {qt.description && <p className="text-xs text-gray-500 truncate">{qt.description}</p>}
                </div>
              </button>
              {canManage && (
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => startEditType(qt)} className="p-1.5 text-gray-400 hover:text-blue-600" title="Edit">
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => toggleTypeMutation.mutate({ id: qt.id, active: qt.is_active })}
                    className="p-1.5 text-gray-400 hover:text-gray-700"
                    title={qt.is_active ? 'Deactivate' : 'Activate'}
                  >
                    {qt.is_active ? <XCircleIcon className="w-4 h-4" /> : <CheckCircleIcon className="w-4 h-4" />}
                  </button>
                </div>
              )}
            </div>

            {expanded[qt.id] && (
              <div className="px-10 pb-4 space-y-2">
                {qt.subtypes.length === 0 && !subtypeFormFor && (
                  <p className="text-xs text-gray-400">No sub-types.</p>
                )}
                {qt.subtypes.map((st) => (
                  <div key={st.id} className={`flex items-center justify-between py-1.5 border-b border-gray-50 last:border-0 ${!st.is_active ? 'opacity-60' : ''}`}>
                    <div>
                      <span className="text-sm text-gray-800">{st.name}</span>
                      {st.default_duration_minutes != null && (
                        <span className="text-xs text-gray-400 ml-2">{st.default_duration_minutes} min</span>
                      )}
                      {st.description && <p className="text-xs text-gray-500">{st.description}</p>}
                    </div>
                    {canManage && (
                      <div className="flex items-center gap-1">
                        <button onClick={() => startEditSubtype(st)} className="p-1 text-gray-400 hover:text-blue-600">
                          <PencilIcon className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => toggleSubtypeMutation.mutate({ id: st.id, active: st.is_active })}
                          className="p-1 text-gray-400 hover:text-gray-700"
                        >
                          {st.is_active ? <XCircleIcon className="w-3.5 h-3.5" /> : <CheckCircleIcon className="w-3.5 h-3.5" />}
                        </button>
                      </div>
                    )}
                  </div>
                ))}

                {subtypeFormFor === qt.id ? (
                  <div className="mt-2 p-3 bg-gray-50 rounded-md space-y-2">
                    <input
                      value={subtypeForm.name}
                      onChange={(e) => setSubtypeForm({ ...subtypeForm, name: e.target.value })}
                      placeholder="Sub-type name"
                      className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                    />
                    <textarea
                      value={subtypeForm.description}
                      onChange={(e) => setSubtypeForm({ ...subtypeForm, description: e.target.value })}
                      placeholder="Description"
                      rows={2}
                      className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                    />
                    <input
                      type="number" min={1} max={180}
                      value={subtypeForm.default_duration_minutes}
                      onChange={(e) => setSubtypeForm({ ...subtypeForm, default_duration_minutes: e.target.value })}
                      placeholder={`Duration override (default: ${qt.default_duration_minutes} min)`}
                      className="w-full border border-gray-300 rounded-md px-2 py-1 text-sm"
                    />
                    <div className="flex gap-2">
                      <button onClick={() => submitSubtypeForm(qt.id)} className="px-2.5 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700">
                        {editingSubtype ? 'Save' : 'Add'}
                      </button>
                      <button onClick={closeSubtypeForm} className="px-2.5 py-1 bg-gray-200 text-gray-700 rounded text-xs hover:bg-gray-300">
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : canManage && (
                  <button
                    onClick={() => { setSubtypeFormFor(qt.id); setEditingSubtype(null); setSubtypeForm(EMPTY_SUBTYPE_FORM) }}
                    className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1 mt-1"
                  >
                    <PlusIcon className="w-3 h-3" /> Add Sub-type
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}

// ============================================================================
// History (audit log) modal — who created/edited/activated/deactivated a
// question type or subtype, and when. Org-wide, not per-type.
// ============================================================================

const AUDIT_ACTION_LABELS: Record<string, string> = {
  created: 'Created',
  updated: 'Updated',
  activated: 'Activated',
  deactivated: 'Deactivated',
}

function HistoryModal({ onClose }: { onClose: () => void }) {
  const { data: entries, isLoading } = useQuery({
    queryKey: ['question-type-audit-log'],
    queryFn: getQuestionTypeAuditLog,
    // The app's default QueryClient staleTime is 5 minutes (App.tsx), so
    // without this, reopening the modal shortly after a change just shows
    // the stale cached list instead of hitting the server again.
    refetchOnMount: 'always',
    staleTime: 0,
  })

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Question Type History</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="w-5 h-5" /></button>
        </div>
        {isLoading ? (
          <div className="flex justify-center py-8"><LoadingSpinner size="md" /></div>
        ) : !entries || entries.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">No history yet.</p>
        ) : (
          <div className="space-y-3">
            {entries.map((entry: QuestionTypeAuditLogEntry, idx: number) => (
              <div key={idx} className="flex gap-3 text-sm">
                <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded flex-shrink-0 mt-0.5">
                  {entry.entity_type === 'question_subtype' ? 'Sub-type' : 'Type'}
                </span>
                <div className="min-w-0">
                  <p className="text-gray-900">
                    {AUDIT_ACTION_LABELS[entry.action] || entry.action} <span className="font-medium">{entry.entity_name}</span>
                  </p>
                  {entry.action === 'updated' && Array.isArray(entry.details?.fields) && (
                    <p className="text-xs text-gray-500">Changed: {entry.details.fields.join(', ')}</p>
                  )}
                  <p className="text-xs text-gray-400">
                    {entry.changed_by_name || 'Unknown user'} · {new Date(entry.created_at).toLocaleString(undefined, {
                      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                    })}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default QuestionTypeManagementPage
